import { createContext, useContext } from 'react';
import { useAuthUserOrNull } from '@frontegg/react';
import { Query } from '@tanstack/react-query';

import { AccountsResponse } from '../utils/types';

export const TENANT_ID_STORAGE_KEY = 'tenantId';
export const CONFIG_REDIRECT_PENDING_KEY = 'configRedirectPending';

const ACCOUNT_SPECIFIC_QUERY_KEYS = [
  'currentUtilizationAvg',
  'volumesCapacity',
  'totalVolumeCount',
  'monitoredVolumeCount',
  'autoscalingVolumeCount',
  'monitoredCapacity',
  'autoscalingCapacity',
  'autoscaleRuleDetails',
  'autoscaleRules',
  'getVolume',
  'getOptimizationActions',
  'configurationTree',
  'getLastDay',
  'ruleDryRun',
] as const;

export const createAccountSpecificQueryPredicate = (removedQueries: string[]) => (query: Query) => {
  const key = query.queryKey;
  if (!Array.isArray(key) || key.length === 0) {
    return false;
  }

  const firstKey = key[0];

  // Keep cross-tenant queries
  if (firstKey === 'getRoleArn' || firstKey === 'getTenants' || firstKey === 'getAccounts') {
    return false;
  }

  // Remove account-specific queries
  if (typeof firstKey === 'string') {
    // API endpoints (covers most data fetching)
    if (firstKey.startsWith('/api/v1/')) {
      removedQueries.push(`API: ${firstKey}`);
      return true;
    }

    if (
      firstKey === 'configuration' ||
      firstKey === 'configurationTree' ||
      firstKey.startsWith('configuration')
    ) {
      removedQueries.push(`Config: ${JSON.stringify(key)}`);
      return true;
    }

    // Report queries using dynamic URLs (savings stats, utilization, etc.)
    if (firstKey.includes('http') || firstKey.includes('/api')) {
      removedQueries.push(`Report: ${firstKey}`);
      return true;
    }

    // Fleet manager volume queries with JSON filters
    if (firstKey.includes('/api/v1/volumes') && key.length > 1 && typeof key[1] === 'string') {
      try {
        const parsed = JSON.parse(key[1]);
        if (parsed && (parsed.filters || parsed.sortField || parsed.pageOffset !== undefined)) {
          removedQueries.push(`VolumeQuery: ${firstKey}`);
          return true;
        }
      } catch {
        // Not JSON, continue with other checks
      }
    }

    if (
      ACCOUNT_SPECIFIC_QUERY_KEYS.includes(firstKey as (typeof ACCOUNT_SPECIFIC_QUERY_KEYS)[number])
    ) {
      removedQueries.push(`KPI: ${JSON.stringify(key)}`);
      return true;
    }
  }

  return false;
};

// Define AuthContextValue by combining auth state with tenant and account data
export interface AuthContextValue {
  user: ReturnType<typeof useAuthUserOrNull>;

  // tenants?: TenantsResponse['tenants'];
  // activeTenant?: TenantsResponse['activeTenant'];
  accounts?: AccountsResponse['accounts'];
  // activeAccountId?: AccountsResponse['activeAccountId']; // accountRes?.activeAccountId does not update when a tab is changed
  // activeAccountName?: AccountsResponse['accounts'][number]['accountName'];
  activeTenantName?: string;
  activeTenantId?: string;

  error?: Error;
  loading?: boolean;

  isAuthenticated: boolean;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Hook to access the AuthContext.
 * Provides access to all auth, tenant, and account data.
 *
 * @throws Error if used outside of AuthContextProvider
 */
export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthContextProvider');
  }
  return context;
}
